import { Link } from "react-router-dom";
import type { ShapeInfo } from "../../rdf/types";

function ConstraintTable({ shape }: { shape: ShapeInfo }) {
  return (
    <div className="mb-4 rounded-md border border-[var(--oe-border)]">
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--oe-border)] bg-[var(--oe-surface)] px-3 py-2 text-sm">
        <span className="font-medium">{shape.shapePrefixed}</span>
        {shape.targetClassIris.length > 0 && (
          <span className="flex items-center gap-1 text-xs text-[var(--oe-text-muted)]">
            targets
            {shape.targetClassIris.map((iri) => (
              <Link
                key={iri}
                to={`/explore/classes?term=${encodeURIComponent(iri)}`}
                className="text-[var(--oe-accent)] hover:underline"
              >
                {iri.split(/[/#]/).pop()}
              </Link>
            ))}
          </span>
        )}
      </div>
      {shape.properties.length === 0 ? (
        <p className="px-3 py-3 text-sm text-[var(--oe-text-muted)]">
          No property constraints declared.
        </p>
      ) : (
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[var(--oe-text-muted)]">
              <th className="py-1.5 pl-3 pr-3">Path</th>
              <th className="py-1.5 pr-3">Type</th>
              <th className="py-1.5 pr-3">Cardinality</th>
              <th className="py-1.5 pr-3">Pattern</th>
            </tr>
          </thead>
          <tbody>
            {shape.properties.map((constraint, index) => (
              <tr
                key={`${constraint.pathIri ?? index}`}
                className="border-t border-[var(--oe-border)]/60"
              >
                <td className="py-1.5 pl-3 pr-3 align-top">
                  {constraint.pathIri ? (
                    <Link
                      to={`/explore/properties?term=${encodeURIComponent(constraint.pathIri)}`}
                      className="text-[var(--oe-accent)] hover:underline"
                    >
                      {constraint.pathPrefixed}
                    </Link>
                  ) : (
                    <span className="text-[var(--oe-text-muted)]">(complex path)</span>
                  )}
                  {constraint.name && (
                    <div className="text-xs text-[var(--oe-text-muted)]">{constraint.name}</div>
                  )}
                </td>
                <td className="py-1.5 pr-3 align-top">
                  {constraint.classPrefixed ? (
                    <Link
                      to={`/explore/classes?term=${encodeURIComponent(constraint.classIri!)}`}
                      className="text-[var(--oe-accent)] hover:underline"
                    >
                      {constraint.classPrefixed}
                    </Link>
                  ) : (
                    constraint.datatypePrefixed ?? "—"
                  )}
                </td>
                <td className="py-1.5 pr-3 align-top text-[var(--oe-text-muted)]">
                  {constraint.minCount ?? 0}..{constraint.maxCount ?? "*"}
                </td>
                <td className="py-1.5 pr-3 align-top font-mono text-xs text-[var(--oe-text-muted)]">
                  {constraint.pattern ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

interface ShaclPanelProps {
  shapes: ShapeInfo[];
  targetedByShapes: ShapeInfo[];
}

export default function ShaclPanel({ shapes, targetedByShapes }: ShaclPanelProps) {
  if (shapes.length === 0 && targetedByShapes.length === 0) {
    return (
      <p className="text-sm text-[var(--oe-text-muted)]">
        No SHACL shapes declared for or targeting this term.
      </p>
    );
  }

  return (
    <div>
      {shapes.map((shape) => (
        <ConstraintTable key={shape.shapeIri} shape={shape} />
      ))}
      {targetedByShapes.map((shape) => (
        <ConstraintTable key={shape.shapeIri} shape={shape} />
      ))}
    </div>
  );
}
